import _ from "lodash";
import { SearchOrder } from "../shared/models/search-options.model";
import { Site, ValidityDefinition } from "../shared/models/site.model";
import { PropertyValue } from "../shared/models/metadata.model";
import { SourceItem } from "../shared/models/source-item.model";
import { FileState, FilterState, SiteState, State } from "./state-models";

/***
 * These keys are used in the URL query string to represent the state of the app.
 */
export const urlQueryKeys = {
    semanticFilter: "a",
    baseline: "b",
    customProperties: "c",
    displayProperties: "d",
    distinctSearch: "e",
    /** Legacy bookmark key — not applied when parsing URLs; omit from serialization. Still used by `suppress` link helpers when stripping local highlighting from a copied state snapshot. */
    localSearch: "f",
    organization: "g",
    caseSensitiveSearch: "k",
    lang: "l",
    contentType: "m",
    configuration: "n",
    sortOrder: "o",
    systemProperties: "s",
    textFilter: "t",
    validity: "v",
    wholeWordSearch: "w",
}

const parsePropertyValues = (unparsed: string): Array<PropertyValue> => {
    const array: Array<string> = unparsed.split(":");
    const returned: Array<PropertyValue> = [];
    for (let value of array) {
        const separatorIx = value.indexOf('|');
        const stringValue: string = separatorIx === -1 ? value : value.substring(0, separatorIx);
        const valueType: string = separatorIx === -1 ? "" : value.substring(separatorIx + 1);
        switch (valueType) {
            case "number":
                returned.push(parseInt(stringValue));
                break;
            case "boolean":
                returned.push(stringValue.toLowerCase() === "true");
                break;
            case "date":
                returned.push(Date.parse(stringValue));
                break;
            case "string":
            default:
                returned.push(stringValue);
                break;
        }
    }
    return returned;
}

// const setStringValue = (
//     dispatch: React.Dispatch<ComponentStateStringAction>,
//     action: ComponentStateStringAction["type"],
//     currentValue: any,
//     value: string) => {
//     if (currentValue != value) {
//         dispatch({ type: action, payload: value });
//     }
// }

// const setPropertiesValue = (
//     dispatch: React.Dispatch<ComponentStatePropertiesAction>,
//     action: ComponentStatePropertiesAction["type"],
//     currentValue: any,
//     property: string,
//     value: string) => {
//     const propertyValue: PropertyValue[] = parsePropertyValues(value);
//     if (!_.isEqual(currentValue[property], propertyValue)) {
//         dispatch({ type: action, payload: { [property]: propertyValue } });
//     }
// }

export function getLocationItemFromState(state: State): SourceItem {
    const item: SourceItem = {
        state: {},
        value: (state as FileState).path || "",
        position: 1,
        props: {
            other: {
                text: [(state as FilterState).text || ""],
                semantic: [(state as FilterState).semantic || ""],
                contentType: [(state as FilterState).contentType || ""],
                path: [(state as FileState).path || ""],
                site: [(state as SiteState).site?.name || ""],
                sitePath: [(state as SiteState).sitePath || ""],
                validity: [(state as FilterState).validity || ""],
                baseline: [(state as FilterState).baseline || ""],
                localSearch: [(state as FilterState).localSearch || ""],
                caseSensitiveSearch: [(state as FilterState).caseSensitiveSearch || ""],
                wholeWordSearch: [(state as FilterState).wholeWordSearch || ""],
            },
            custom: (state as FilterState).customProperties || {},
            system: (state as FilterState).systemProperties || {},
            display: (state as FilterState).displayProperties || {},
        },
    };
    if ((state as FilterState).organization) {
        item.props.info = { organization: [(state as FilterState).organization || ""] };
    }
    if ((state as FilterState).configuration) {
        item.props.info = { ...item.props.info, configuration: [(state as FilterState).configuration || ""] };
    }
    return item;
}

export function updateStateFromLocation(
    location: string,
    currentState: FilterState,
): FilterState {
    const urlParams: URLSearchParams = new URLSearchParams(location);
    const fixedUrlParams: URLSearchParams = new URLSearchParams();
    for (let [key, value] of urlParams.entries()) {
        if (key.indexOf("?") !== -1) {
            // The first argument includes the path and the query param for some reason
            key = key.substring(key.indexOf("?") + 1);
        }
        fixedUrlParams.set(key, value);
    }
    return updateStateFromSearchParams(fixedUrlParams, currentState);
}

export function updateStateFromSearchParams(
    searchParams: URLSearchParams,
    currentState: FilterState,
): FilterState {
    let newState: FilterState = _.cloneDeep(currentState);
    searchParams.forEach((value, key) => {
        if (key.indexOf(".") !== -1) {
            const parts = key.split(".");
            const propertyName: string = parts[0];
            const propertyValue: string = parts[1];
            switch (propertyName) {
                case urlQueryKeys.customProperties:
                    newState.customProperties = newState.customProperties || {};
                    newState.customProperties[propertyValue] = parsePropertyValues(value);
                    break;
                case urlQueryKeys.systemProperties:
                    newState.systemProperties = newState.systemProperties || {};
                    newState.systemProperties[propertyValue] = parsePropertyValues(value);
                    break;
                case urlQueryKeys.displayProperties:
                    newState.displayProperties = newState.displayProperties || {};
                    newState.displayProperties[propertyValue] = parsePropertyValues(value);
                    break;
            }
            return;
        }
        else switch (key) {
            case urlQueryKeys.textFilter:
                newState.text = value;
                break;
            case urlQueryKeys.semanticFilter:
                newState.semantic = value;
                break;
            case urlQueryKeys.validity:
                newState.validity = value;
                break;
            case urlQueryKeys.lang:
                newState.lang = value;
                break;
            case urlQueryKeys.baseline:
                newState.baseline = value;
                break;
            case urlQueryKeys.organization:
                newState.organization = value;
                break;
            case urlQueryKeys.configuration:
                newState.configuration = value;
                break;
            case urlQueryKeys.sortOrder:
                const orders: Array<string> = value.split(":");
                const orderList: Array<SearchOrder> = [];
                for (let order of orders) {
                    const parts: Array<string> = order.split(".");
                    if (parts.length === 3 && (parts[2] === "1" || parts[2] === "-1")) {
                        orderList.push({ propertyType: parts[0], propertyName: parts[1], direction: parts[2] })
                    }
                }
                if (orderList.length) {
                    if (!_.isEqual(newState.searchOptions?.order, orderList)) {
                        newState.searchOptions = { ...newState.searchOptions, order: orderList };
                    }
                }
                break;
            case urlQueryKeys.distinctSearch:
                const distinct = value !== "false";
                if (newState.searchOptions?.distinct !== distinct) {
                    newState.searchOptions = { ...newState.searchOptions, order: newState.searchOptions?.order ?? [], distinct };
                }
                break;
            case urlQueryKeys.contentType:
                newState.contentType = value;
                break;
            case urlQueryKeys.caseSensitiveSearch:
                newState.caseSensitiveSearch = value === "true";
                break;
            case urlQueryKeys.wholeWordSearch:
                newState.wholeWordSearch = value === "true";
                break;
            // Legacy key "f" (urlQueryKeys.localSearch) is ignored: localSearch is not URL-backed.
        }
    });
    const textBeforeParse = currentState.text;
    if (textBeforeParse !== newState.text) {
        newState = { ...newState, localSearch: newState.text };
    }
    return newState;
}

export function applyDefaults(
    currentState: FilterState,
    site: Site,
): void {
    // Apply default validity if needed
    const currentValidity: Array<string> = (currentState.validity || "").split(":");
    const path: Array<string> = [];
    if (currentValidity[0]?.startsWith("$")) {
        // Don't include values starting with $ because they are preview validities
        currentValidity.shift();
    }
    let definition: ValidityDefinition | null = site.validities || null;
    while (definition) {
        const value = currentValidity.shift() || definition.default || "";
        if (value) {
            path.push(value);
            definition = definition.values.find(v => v.value === value)?.definition || null;
        }
        else {
            break;
        }
    }
    if (path.length) {
        currentState.validity = path.join(":");
    }

    // Look for preferred language in custom language mappings first
    // TODO: How do we handle browser default language when we use SSR?
    // if (!currentState.systemProperties?.lang?.length) {
    //     const languagePreferences = window?.navigator.languages;
    //     if (languagePreferences && languagePreferences.length) {
    //         const preferredLanguage = languagePreferences.find(pref => site?.languages?.find(lang => lang.mappings?.includes(pref)));
    //         if (preferredLanguage) {
    //             currentState.systemProperties = { lang: [preferredLanguage] };
    //             currentState.defaultSystemProps = { lang: [preferredLanguage] };
    //         }
    //     }
    // }
    // Or fallback to common default language
    if (!currentState.lang?.length) {
        const defaultLanguage = site?.languages?.find(lang => lang.isDefault);
        if (defaultLanguage) {
            currentState.lang = defaultLanguage.code;
        }
    }

    // Add defined default custom properties if needed
    if (site?.defaults?.customProperties) {
        for (let propName in site?.defaults.customProperties) {
            if (!currentState.customProperties?.[propName]) {
                currentState.customProperties = currentState.customProperties || {};
                currentState.customProperties[propName] = [site?.defaults.customProperties[propName]];
                currentState.defaultCustomProps = currentState.defaultCustomProps || {};
                currentState.defaultCustomProps[propName] = [site?.defaults.customProperties[propName]];
            }
        }
    }

}

const stringifyArray = (array: Array<PropertyValue>, delimiter: string): string => {
    let result = "";
    for (let i = 0; i < array.length; i++) {
        i !== 0 && (result += delimiter);
        if (typeof array[i] === "number") {
            result += `${array[i].toString()}|number`;
        }
        else if (typeof array[i] === "boolean") {
            result += `${array[i].toString()}|boolean`;
        }
        else if (array[i] instanceof Date) {
            result += `${(array[i] as Date).getMilliseconds()}|date`;
        }
        else if (typeof array[i] === "string") {
            result += array[i];
        }
    }
    return result;
}


export function getStateAsLocationQuery(state: FilterState): URLSearchParams {
    const urlParams = new URLSearchParams();

    if (state.validity) {
        urlParams.set(urlQueryKeys.validity, state.validity);
    }
    if (state.lang) {
        urlParams.set(urlQueryKeys.lang, state.lang);
    }
    if (state.baseline) {
        urlParams.set(urlQueryKeys.baseline, state.baseline);
    }
    for (const name of Object.keys(state.systemProperties ?? {})) {
        const currentVals = state.systemProperties?.[name];
        const defaultVals = state.defaultSystemProps?.[name];
        if (defaultVals !== undefined && _.isEqual(defaultVals, currentVals)) {
            continue;
        }
        urlParams.set(urlQueryKeys.systemProperties + "." + name, stringifyArray(currentVals ?? [], ":"));
    }
    for (const name of Object.keys(state.customProperties ?? {})) {
        const currentVals = state.customProperties?.[name];
        const defaultVals = state.defaultCustomProps?.[name];
        if (defaultVals !== undefined && _.isEqual(defaultVals, currentVals)) {
            continue;
        }
        urlParams.set(urlQueryKeys.customProperties + "." + name, stringifyArray(currentVals ?? [], ":"));
    }
    for (const name of Object.keys(state.displayProperties ?? {})) {
        urlParams.set(urlQueryKeys.displayProperties + "." + name, stringifyArray(state.displayProperties?.[name] ?? [], ":"));
    }
    if (state.text) {
        urlParams.set(urlQueryKeys.textFilter, state.text);
    }
    if (state.semantic) {
        urlParams.set(urlQueryKeys.semanticFilter, state.semantic);
    }
    if (state.organization) {
        urlParams.set(urlQueryKeys.organization, state.organization);
    }
    if (state.configuration) {
        urlParams.set(urlQueryKeys.configuration, state.configuration);
    }
    if (state.searchOptions?.order && state.searchOptions?.order.length && !state.defaultSearchOptions?.includes("order")) {
        let orderArray: Array<string> = [];
        for (let orderDef of state.searchOptions?.order) {
            orderArray.push(`${orderDef.propertyType}.${orderDef.propertyName}.${orderDef.direction}`);
        }
        urlParams.set(urlQueryKeys.sortOrder, stringifyArray(orderArray, ":"));
    }
    if (state.searchOptions?.distinct === false && !state.defaultSearchOptions?.includes("distinct")) {
        urlParams.set(urlQueryKeys.distinctSearch, "false");
    }
    if (state.caseSensitiveSearch) {
        urlParams.set(urlQueryKeys.caseSensitiveSearch, state.caseSensitiveSearch.toString());
    }
    if (state.wholeWordSearch) {
        urlParams.set(urlQueryKeys.wholeWordSearch, state.wholeWordSearch.toString());
    }

    return urlParams;
}
